import {
  RemoteParticipant as OfficialTwilioRemoteParticipant,
  Room as OfficialTwilioRoom,
  TwilioError,
} from 'twilio-video';

import { RoomEvent, RoomEventHandlerMap } from '../../core/room/types';
import { handleUnhandledEventHandlerException } from '../../core/utils';
import { TwilioRoom } from './RoomDelegator';
import {
  STANDARD_ROOM_EVENT_TO_TWILIO_ROOM_EVENT_MAP,
  StandardRoomEventToTwilioRoomEventMap,
  TwilioRoomEventHandler,
} from './events';
import { TwilioRemoteParticipant } from './participant/RemoteParticipant';

export function isEventSupported(event: string): event is RoomEvent {
  return event in STANDARD_ROOM_EVENT_TO_TWILIO_ROOM_EVENT_MAP;
}

export function getTwilioRoomEvent<T extends RoomEvent>(
  standardEvent: T,
): StandardRoomEventToTwilioRoomEventMap[T] | undefined {
  return STANDARD_ROOM_EVENT_TO_TWILIO_ROOM_EVENT_MAP[standardEvent];
}

function wrapRoomParticipantEventHandler(handler: RoomEventHandlerMap['participantConnected']) {
  return function handleParticipantConnected(remoteParticipant: OfficialTwilioRemoteParticipant) {
    const participant = new TwilioRemoteParticipant(remoteParticipant);
    handler(participant);
  } satisfies TwilioRoomEventHandler<'participantConnected'>;
}

function wrapRoomDominantSpeakerChangedEventHandler(handler: RoomEventHandlerMap['dominantSpeakerChanged']) {
  return function handleDominantSpeakerChanged(remoteParticipant: OfficialTwilioRemoteParticipant | null) {
    if (remoteParticipant) {
      const participant = new TwilioRemoteParticipant(remoteParticipant);
      handler(participant);
    }
    handler(null);
  } satisfies TwilioRoomEventHandler<'dominantSpeakerChanged'>;
}

function wrapRoomDisconnectedEventHandler(handler: RoomEventHandlerMap['disconnected']) {
  return function handleDisconnected(room: OfficialTwilioRoom, error: TwilioError) {
    const result = handler(new TwilioRoom(room), error, {
      // ℹ️ Rejoinability is currently not supported for Twilio rooms.
      // Implementation would likely require changes to the room lifecycle
      // management to support something akin to Zoom's failover state. That
      // being said, Twilio does have built-in reconnect handling already which
      // usually suffices to prevent a full disconnect.
      // https://devforum.zoom.us/t/how-to-handle-internet-disconnection-properly/65018
      isRoomRejoinable: false,
    });
    handleUnhandledEventHandlerException(result, 'Twilio: disconnected');
  } satisfies TwilioRoomEventHandler<'disconnected'>;
}

function wrapRoomReconnectingEventHandler(handler: RoomEventHandlerMap['reconnecting']) {
  return function handleReconnecting(error: TwilioError) {
    handler(error);
  } satisfies TwilioRoomEventHandler<'reconnecting'>;
}

function wrapRoomReconnectedEventHandler(handler: RoomEventHandlerMap['reconnected']) {
  return function handleReconnected() {
    handler();
  } satisfies TwilioRoomEventHandler<'reconnected'>;
}

export function createTwilioRoomEventHandler<K extends RoomEvent>(event: K, handler: RoomEventHandlerMap[K]) {
  switch (event) {
    case 'participantConnected':
    case 'participantReconnecting':
    case 'participantReconnected':
    case 'participantDisconnected':
      return wrapRoomParticipantEventHandler(handler as RoomEventHandlerMap['participantConnected']);

    case 'dominantSpeakerChanged':
      return wrapRoomDominantSpeakerChangedEventHandler(handler as RoomEventHandlerMap['dominantSpeakerChanged']);

    case 'disconnected':
      return wrapRoomDisconnectedEventHandler(handler as RoomEventHandlerMap['disconnected']);

    case 'reconnecting':
      return wrapRoomReconnectingEventHandler(handler as RoomEventHandlerMap['reconnecting']);

    case 'reconnected':
      return wrapRoomReconnectedEventHandler(handler as RoomEventHandlerMap['reconnected']);

    default: {
      const _exhaustiveCheck: never = event;
      throw new Error(`Unsupported event: ${_exhaustiveCheck}`);
    }
  }
}
